import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeadSub from '../../../../components/comm/HeadSub';
import URL from '../../../../constants/url';
import { STEPS_STATUS, useHealthSteps } from '../../../../hook/useHealthSteps';
import { openSamsungHealthStore } from '../../../../bridge/healthBridge';
import style from './StepsMain.module.css';
import imgSteps from '../../../../images/common/img_3d_steps_main.png';

/**
 * 걸음수 연동 안내 화면.
 * 여기서는 삼성 헬스 사용 가능 여부와 권한만 확인하고, 걸음수 자체는 안 불러온다
 * (autoLoad: false). 데이터는 연동이 끝난 뒤 대시보드에서 조회한다.
 */
const StepsMain = () => {
    const navigate = useNavigate();
    const { status, message, reason, permissionDenied, connect, reload } =
        useHealthSteps({ autoLoad: false, sync: false });
    const [connecting, setConnecting] = useState(false);

    const blocked = status === STEPS_STATUS.UNAVAILABLE;
    const needsInstall = blocked && reason === 'APP_NOT_INSTALLED';

    // 스토어에서 설치하고 돌아오면 다시 판정한다.
    useEffect(() => {
        if (!needsInstall) return undefined;
        const onVisible = () => {
            if (document.visibilityState === 'visible') reload();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [needsInstall, reload]);

    // 이미 연동돼 있으면 이 화면은 할 일이 없다. 뒤로가기로 다시 튕기지 않게 replace.
    useEffect(() => {
        if (status === STEPS_STATUS.READY) {
            navigate(URL.STEPS_DASHBOARD, { replace: true });
        }
    }, [status, navigate]);

    const handleConnect = async () => {
        setConnecting(true);
        const granted = await connect();
        setConnecting(false);
        if (granted) {
            navigate(URL.STEPS_DASHBOARD, { replace: true });
        }
    };

    const guideText = () => {
        if (status === STEPS_STATUS.CHECKING) return '연동 상태를 확인하고 있어요…';
        if (blocked || status === STEPS_STATUS.ERROR) return message;
        if (permissionDenied) {
            return '삼성 헬스에서 걸음 수 접근을 허용해야\n걸음수를 불러올 수 있어요.';
        }
        return '삼성 헬스를 연동하면\n내 걸음수가 자동으로 기록됩니다.';
    };

    const buttonLabel = () => {
        if (connecting) return '삼성 헬스에서 허용해 주세요…';
        if (permissionDenied) return '다시 연동하기';
        return '내 건강 데이터 연동하기';
    };

    return (
        <>
            <HeadSub title="걸음수 기록" />
            <div id="sub-content" data-layout="white">
                <div className="inner-wrap">
                    <div className={style['steps-main-box']}>
                        <div className={style['img-box']}>
                            <img src={imgSteps} alt="" />
                        </div>
                        <p style={{ whiteSpace: 'pre-line' }}>{guideText()}</p>
                    </div>
                    <div className="bottom-fixed-box">
                        <div className="inner-box">
                            <div className="btn-layout" data-btn-num="1">
                                {needsInstall ? (
                                    <button className="btn" data-btn-type="fill-main" data-btn-size="lg"
                                        onClick={openSamsungHealthStore}>
                                        <span>삼성 헬스 설치하기</span>
                                    </button>
                                ) : blocked ? (
                                    <button className="btn" data-btn-type="fill-main-sub" data-btn-size="lg"
                                        onClick={reload}>
                                        <span>다시 확인하기</span>
                                    </button>
                                ) : (
                                    <button className="btn" data-btn-type="fill-main" data-btn-size="lg"
                                        disabled={connecting || status === STEPS_STATUS.CHECKING}
                                        onClick={handleConnect}>
                                        <span>{buttonLabel()}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default StepsMain;
